import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import * as globals from "src/app/globals";
import { BackEndService } from '../backend-service';
import { Notification, NotificationModel, Rule, Widget, WidgetUserFilter } from '../classes';
import { NewsfeedNotification } from '../lib/newsfeed/classes';
import { SessionService } from '../lib/shared/services/session.service';
import { TokenService } from '../lib/shared/services/token.service';
import { User } from '../lib/user/classes';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _zone: NgZone,
    private _backEndService: BackEndService,
    private _dataService: DataService,
    private _sessionService: SessionService
  ) {
    this._sessionService.logoff$.subscribe(() => {
      this._hubConnection.stop();
    });
  }

  public connectionStarted: Subject<void> = new Subject();
  public newsfeedNotificationReceived: Subject<NewsfeedNotification> = new Subject();
  public internalNotificationReceived: Subject<Notification> = new Subject();

  private _issueChanged: Subject<{ date: string, userId: string, identifier: number, message: string }> = new Subject<{ date: string, userId: string, identifier: number, message: string }>();
  public issueChanged$ = this._issueChanged.pipe(debounceTime(1000));

  private _newsfeedNotificationReceived = new Subject<NewsfeedNotification>();
  public newsfeedNotificationReceived$ = this._newsfeedNotificationReceived.asObservable();

  private _onlineUsers = new Subject();
  onlineUsers$ = this._onlineUsers.asObservable();

  private _hubConnection;
  private _proxy;
  public startConnection = () => {

    this._zone.runOutsideAngular(() => {

      // @ts-ignore
      this._hubConnection = $.hubConnection(globals.serverLocation + "/signalr", {
        useDefaultPath: false
      });
      this._hubConnection.qs = { "access_token": this._tokenService.token };
      this._proxy = this._hubConnection.createHubProxy("NotificationHub");

      this._proxy.on("TriggerNewsfeed", (data: NewsfeedNotification) => this._newsfeedNotificationReceived.next(data));

      this._proxy.on("notifyUser", (data: NotificationModel) => {

        switch (data.constructorName) {

          case NotificationTypes.Notification:
            this._httpClient.get(`${globals.serverLocation}/api/notification/${data.objectId}`, {
              headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
            }).subscribe((notification: Notification) => {
              this._dataService.notifications.push(notification);
              this.internalNotificationReceived.next(notification);
            });
            break;

          case NotificationTypes.RoleRight:
            let rule = this._backEndService.rights.find(x => x.ruleid == data.objectId);
            let value = data.value;
            if (!rule && value) {
              this._backEndService.get("api/rule/" + data.objectId).subscribe((rule: Rule) => {
                this._backEndService.rights.push(rule);
                this._backEndService.rightsChanged.next();
              });
            } else if (rule && !value) {
              let i = this._backEndService.rights.indexOf(rule);
              if (i > -1) {
                this._backEndService.rights.splice(i, 1);
                this._backEndService.rightsChanged.next();
              }
            }
            break;

          case NotificationTypes.Widget:
            this._httpClient.get(`${globals.serverLocation}/api/widget/${data.objectId}`, {
              headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
            }).subscribe((widget: Widget) => {
              let i = this._dataService.startPage.findIndex(x => x.widgetid == widget.widgetid);
              if (i > -1) {
                this._dataService.startPage[i] = widget;
                this._dataService.startPageChanged.next();
              } else {
                this._dataService.startPage.push(widget);
                this._dataService.startPageChanged.next();
              }
            });
            break;

          case NotificationTypes.WidgetRemove:
            let i = this._dataService.startPage.findIndex(x => x.widgetid == data.objectId);
            if (i > -1) {
              this._dataService.startPage.splice(i, 1);
              this._dataService.startPageChanged.next();
            }
            break;

          case NotificationTypes.UserFilter:
            this._backEndService.get("api/userfilter/" + data.objectId).subscribe((filter: WidgetUserFilter) => {
              if (filter) {
                this._backEndService.get("api/widget/" + filter.filterwidget).subscribe((widget: Widget) => {
                  let i = this._dataService.startPage.findIndex(x => x.widgetid == widget.widgetid);
                  if (i > -1) {
                    this._dataService.startPage[i] = widget;
                    this._dataService.startPageChanged.next();
                  }
                });
              }
            });
            break;

          case NotificationTypes.UserFilterRemove:
            for (let widget of this._dataService.startPage) {
              for (let filter of widget.widgetuserfilters) {
                if (filter.filterid == data.objectId) {
                  let i = widget.widgetuserfilters.indexOf(filter);
                  if (i > -1) {
                    widget.widgetuserfilters.splice(i, 1);
                    this._dataService.startPageChanged.next();
                    return;
                  }
                }
              }
            }
            break;

          case NotificationTypes.Issue:
            console.log(data);
            this._sessionService.user$.pipe(take(1), filter(x => x && x.id !== data.userId)).subscribe((user: User) => {
              this._issueChanged.next({ date: data.time, identifier: data.objectId, userId: data.userId, message: data.message });
            });
            break;
        }
      });

      this._proxy.on("sendOnlineUsers", (data) => this._onlineUsers.next(data));

      this._hubConnection.start().done(() => {
        this._connectionStarted();
      }).fail({

      });

      this._hubConnection.disconnected(() => {

      })
    });
  }

  public disconnect(){
    console.log(this._hubConnection);
    this._hubConnection.disconnect();
  }

  private _connectionStarted() {
    this._proxy.invoke("getOnlineUsers");
    this.connectionStarted.next();
  }
}

export const NotificationTypes = {
  "Notification": "notification",
  "RoleRight": "roleright",
  "Widget": "widget",
  "WidgetRemove": "widget_remove",
  "UserFilter": "userfilter",
  "UserFilterRemove": "userfilter_remove",
  "Issue": "ISSUE"
}
